/**
 * useDeviceVerificationStatus - Hook for tracking device verification status
 */

import { useState, useEffect, useCallback } from 'react';
import { useMatrixClient } from '../context/MatrixContext';
import { trustEventEmitter, TrustEvents } from '../crypto/events';
import logger from '../utils/logger';

/**
 * Hook to get and track device verification status
 * @param {string} userId - User ID
 * @param {string} deviceId - Device ID
 * @returns {Object} Verification status
 */
export function useDeviceVerificationStatus(userId, deviceId) {
  const client = useMatrixClient();

  const [status, setStatus] = useState({
    isVerified: false,
    isCrossSigned: false,
    lastChangedAt: null,
    isLoading: true,
    error: null,
  });

  // Fetch verification status
  const fetchStatus = useCallback(async () => {
    if (!userId || !deviceId) {
      setStatus(prev => ({
        ...prev,
        isLoading: false,
        error: 'Missing user or device ID',
      }));
      return;
    }

    if (!client) {
      setStatus(prev => ({
        ...prev,
        isLoading: false,
        error: 'Client not initialized',
      }));
      return;
    }

    try {
      const crypto = client.getCrypto();
      if (!crypto) {
        setStatus(prev => ({
          ...prev,
          isLoading: false,
          error: 'Crypto not available',
        }));
        return;
      }

      // Get device verification status
      const verificationStatus = await crypto.getDeviceVerificationStatus(userId, deviceId);

      // Element Web's actual verification logic (based on testing):
      // A device is verified ONLY if it's signed by the owner's cross-signing key
      // OR if cross-signing is fully verified (complete chain)
      // Element Web does NOT consider localVerified as "Verified" status
      // This matches Element's behavior: only signedByOwner or crossSigningVerified = Verified
      const isDeviceVerified = !!(
        verificationStatus?.signedByOwner ||
        verificationStatus?.crossSigningVerified
      );

      const isCrossSigningVerified = !!verificationStatus?.crossSigningVerified;

      setStatus({
        isVerified: isDeviceVerified,
        isCrossSigned: isCrossSigningVerified,
        lastChangedAt: Date.now(),
        isLoading: false,
        error: null,
      });

      logger.debug('useDeviceVerificationStatus: Status fetched', {
        userId,
        deviceId,
        isVerified: verificationStatus?.isVerified(),
      });

    } catch (error) {
      logger.error('useDeviceVerificationStatus: Failed to fetch status:', error);
      setStatus(prev => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  }, [userId, deviceId, client]);

  // Initial fetch
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Listen for trust events
  useEffect(() => {
    const handleTrustEvent = (event) => {
      // Check if event is for our device
      if (event.userId === userId && event.deviceId === deviceId) {
        logger.debug('useDeviceVerificationStatus: Trust event received', event);
        // Refetch status
        fetchStatus();
      }
    };

    // Create stable handler for VERIFICATION_COMPLETE to ensure proper cleanup
    // This fixes a listener leak where inline arrow functions couldn't be unsubscribed
    const handleVerificationComplete = () => {
      // Delay refetch to allow modal UI to settle and avoid visual flashing
      // The modal will close after user clicks OK, then device list updates smoothly
      // Without this delay, multiple DeviceListItem components calling fetchStatus()
      // simultaneously causes cascading re-renders while modal is still visible
      setTimeout(() => {
        fetchStatus();
      }, 500);
    };

    // Subscribe to relevant events
    trustEventEmitter.on(TrustEvents.DEVICE_TRUSTED, handleTrustEvent);
    trustEventEmitter.on(TrustEvents.DEVICE_ERROR, handleTrustEvent);
    trustEventEmitter.on(TrustEvents.VERIFICATION_COMPLETE, handleVerificationComplete);

    return () => {
      trustEventEmitter.off(TrustEvents.DEVICE_TRUSTED, handleTrustEvent);
      trustEventEmitter.off(TrustEvents.DEVICE_ERROR, handleTrustEvent);
      trustEventEmitter.off(TrustEvents.VERIFICATION_COMPLETE, handleVerificationComplete);
    };
  }, [userId, deviceId, fetchStatus]);

  // Note: Removed sync event listener to prevent periodic updates.
  // Device verification status now updates only when:
  // 1. Hook is initially mounted/device changes
  // 2. Trust events are received (device verified, verification complete, etc.)
  // This eliminates unnecessary API calls while maintaining real-time updates for actual changes.

  return status;
}

/**
 * Hook to get current device verification status
 * @returns {Object} Current device verification status
 */
export function useCurrentDeviceVerificationStatus() {
  const client = useMatrixClient();
  const userId = client?.getUserId();
  const deviceId = client?.getDeviceId();

  return useDeviceVerificationStatus(userId, deviceId);
}